// Dedicated import flow for Apollo.io exports.
//
// Three steps: Upload  →  Preview (with dedup + issues)  →  Import.
//
// Differences vs the generic Excel importer in /pipeline:
//   • Auto-maps Notes → contactTitle (Apollo's quirk).
//   • Treats stage "Unqualified" as Prospect.
//   • Defaults Lead Source to "Apollo".
//   • Cross-checks every row against the database for duplicate Lead ID
//     and email *before* committing, and shows the user the breakdown.

import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { parseLeadsWorkbook } from '../lib/importExcel';
import { bulkCreateLeads, findExistingLeadKeys } from '../lib/leads';

const STEPS = ['Upload', 'Preview', 'Done'];

export default function ApolloImport() {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);   // { leads, issues, columns, knownColumns, unknownColumns }
  const [dupes, setDupes]   = useState(null);   // { legacyIds: Set, emails: Set }
  const [busy, setBusy]     = useState(false);
  const [result, setResult] = useState(null);   // { created, skipped }
  const [error, setError]   = useState('');
  const fileRef = useRef(null);

  async function onPickFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(''); setBusy(true);
    try {
      setFile(f);
      const p = await parseLeadsWorkbook(f, { source: 'apollo' });
      setParsed(p);

      // Look up duplicates against the live DB so the preview shows the truth.
      const legacyIds = p.leads.map(l => l.legacyId).filter(Boolean);
      const emails    = p.leads.map(l => l.email).filter(Boolean);
      if (legacyIds.length || emails.length) {
        const dup = await findExistingLeadKeys({ legacyIds, emails });
        setDupes(dup);
      } else {
        setDupes({ legacyIds: new Set(), emails: new Set() });
      }
      setStep(1);
    } catch (err) {
      setError(err.message || 'Could not read the file.');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  }

  async function commit() {
    if (!parsed) return;
    setBusy(true); setError('');
    try {
      const r = await bulkCreateLeads(parsed.leads, { skipDuplicates: true });
      setResult(r);
      setStep(2);
    } catch (err) {
      setError(err.message || 'Import failed. Try again or contact your admin.');
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setStep(0); setFile(null); setParsed(null); setDupes(null); setResult(null); setError('');
  }

  // --------- counts for the preview ---------
  let willCreate = 0, willSkip = 0;
  if (parsed && dupes) {
    for (const l of parsed.leads) {
      const dup = (l.legacyId && dupes.legacyIds.has(String(l.legacyId)))
               || (l.email    && (dupes.emails.has(String(l.email)) || dupes.emails.has(String(l.email).toLowerCase())));
      if (dup) willSkip++;
      else     willCreate++;
    }
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Apollo Lead Import</h1>
          <p className="text-sm text-slate-500">Upload an Apollo.io export to add new leads to the CRM in bulk.</p>
        </div>
        <Link to="/pipeline" className="btn-ghost border border-slate-200">← Back to Pipeline</Link>
      </div>

      <Stepper step={step} />

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
          {error}
        </div>
      )}

      {step === 0 && (
        <UploadStep
          busy={busy}
          fileRef={fileRef}
          onPickFile={onPickFile}
        />
      )}

      {step === 1 && parsed && dupes && (
        <PreviewStep
          file={file}
          parsed={parsed}
          dupes={dupes}
          willCreate={willCreate}
          willSkip={willSkip}
          busy={busy}
          onConfirm={commit}
          onCancel={reset}
        />
      )}

      {step === 2 && result && (
        <DoneStep result={result} onAnother={reset} />
      )}
    </div>
  );
}

/* ------------------- Stepper ------------------- */
function Stepper({ step }) {
  return (
    <div className="flex items-center gap-2 mb-6">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold ${
            i < step ? 'bg-b1s-500 text-white'
            : i === step ? 'bg-brand-700 text-white'
            : 'bg-slate-200 text-slate-500'
          }`}>
            {i < step ? '✓' : i + 1}
          </div>
          <div className={`text-sm ${i === step ? 'font-semibold text-brand-700' : 'text-slate-500'}`}>{s}</div>
          {i < STEPS.length - 1 && <div className="w-8 h-px bg-slate-300 mx-2" />}
        </div>
      ))}
    </div>
  );
}

/* ------------------- Step 1: Upload ------------------- */
function UploadStep({ busy, fileRef, onPickFile }) {
  return (
    <div className="card p-6">
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <h2 className="font-semibold mb-2">Drop your Apollo export</h2>
          <p className="text-sm text-slate-600 mb-4">
            Export your Apollo.io list as Excel, then drop it here.
            We accept the standard B1S template format <span className="font-mono">.xlsx</span>.
          </p>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="btn-primary w-full"
          >
            {busy ? 'Reading file…' : 'Choose Excel file'}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={onPickFile}
            className="hidden"
          />
          <div className="mt-4 text-xs text-slate-500">
            Need the template?{' '}
            <a
              href="/templates/B1S-Leads-Import-Template.xlsx"
              download
              className="text-brand-700 underline"
            >
              Download the empty B1S Apollo template
            </a>
          </div>
        </div>
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <div className="font-semibold text-sm mb-2">What this importer does for you</div>
          <ul className="text-sm text-slate-700 space-y-1.5 list-disc pl-5">
            <li>Maps Apollo's <span className="font-mono">Notes</span> column to <strong>Contact Title</strong>.</li>
            <li>Treats stage <span className="font-mono">Unqualified</span> as <strong>Prospect</strong>.</li>
            <li>Sets Lead Source to <strong>Apollo</strong> automatically.</li>
            <li>Cleans phone numbers and parses <span className="font-mono">DD.MM.YYYY</span> dates.</li>
            <li>Skips duplicates by Lead ID or email — no double cards on your kanban.</li>
            <li>Sets <strong>you</strong> as the owner of every imported lead.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ------------------- Step 2: Preview ------------------- */
function PreviewStep({ file, parsed, dupes, willCreate, willSkip, busy, onConfirm, onCancel }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Stat label="Rows in file"           value={parsed.leads.length} />
        <Stat label="Will be imported"       value={willCreate} accent="b1s" />
        <Stat label="Will be skipped (duplicates)" value={willSkip} accent={willSkip ? 'amber' : 'slate'} />
      </div>

      {parsed.unknownColumns.length > 0 && (
        <div className="card p-4 border-amber-200 bg-amber-50">
          <div className="font-semibold text-amber-800 text-sm mb-1">
            ⚠ Unrecognised columns ({parsed.unknownColumns.length})
          </div>
          <div className="text-xs text-amber-800">
            These columns from your file will be ignored: {parsed.unknownColumns.join(', ')}
          </div>
        </div>
      )}

      {parsed.issues.length > 0 && (
        <div className="card p-4 border-amber-200 bg-amber-50">
          <div className="font-semibold text-amber-800 text-sm mb-1">
            Notes from the importer ({parsed.issues.length})
          </div>
          <ul className="text-xs text-amber-800 list-disc pl-5 space-y-0.5">
            {parsed.issues.slice(0, 8).map((i, k) => <li key={k}>{i}</li>)}
            {parsed.issues.length > 8 && <li>…and {parsed.issues.length - 8} more (silently applied).</li>}
          </ul>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 text-sm font-semibold flex items-center justify-between">
          <span>Preview — first 10 rows from <span className="font-mono">{file.name}</span></span>
          <span className="text-xs text-slate-500 font-normal">{parsed.sheetName} sheet</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-100 text-slate-600">
              <tr>
                <Th>Status</Th>
                <Th>Lead ID</Th>
                <Th>Company</Th>
                <Th>Contact</Th>
                <Th>Title</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>Stage</Th>
                <Th>Source</Th>
              </tr>
            </thead>
            <tbody>
              {parsed.leads.slice(0, 10).map((l, i) => {
                const dup = (l.legacyId && dupes.legacyIds.has(String(l.legacyId)))
                         || (l.email    && (dupes.emails.has(String(l.email)) || dupes.emails.has(String(l.email).toLowerCase())));
                return (
                  <tr key={i} className={`border-t border-slate-100 ${dup ? 'bg-amber-50/50' : ''}`}>
                    <Td>{dup
                      ? <span className="chip bg-amber-100 text-amber-800">skip</span>
                      : <span className="chip bg-emerald-100 text-emerald-800">new</span>}</Td>
                    <Td>{l.legacyId || ''}</Td>
                    <Td className="font-medium text-slate-800">{l.companyName}</Td>
                    <Td>{l.contactName}</Td>
                    <Td className="text-slate-600">{l.contactTitle}</Td>
                    <Td>{l.email}</Td>
                    <Td>{l.phone}</Td>
                    <Td>{l.stage}</Td>
                    <Td>{l.leadSource}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {parsed.leads.length > 10 && (
          <div className="px-4 py-2 text-xs text-slate-500 border-t border-slate-100 bg-slate-50">
            …and {parsed.leads.length - 10} more rows.
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-2">
        <button onClick={onCancel} className="btn-ghost border border-slate-200">Start over</button>
        <button onClick={onConfirm} disabled={busy || willCreate === 0} className="btn-primary">
          {busy ? 'Importing…' : `Import ${willCreate} lead${willCreate === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  );
}

/* ------------------- Step 3: Done ------------------- */
function DoneStep({ result, onAnother }) {
  return (
    <div className="card p-6 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-b1s-100 text-b1s-700 text-xl mb-3">✓</div>
      <h2 className="text-lg font-semibold">Import complete</h2>
      <p className="text-sm text-slate-600 mt-1">
        Created <strong>{result.created.length}</strong> new lead{result.created.length === 1 ? '' : 's'}
        {result.skipped.length > 0 && <> · skipped <strong>{result.skipped.length}</strong> duplicate{result.skipped.length === 1 ? '' : 's'}</>}.
      </p>

      {result.skipped.length > 0 && (
        <div className="mt-4 text-left text-sm">
          <div className="font-semibold text-slate-700 mb-1">Skipped duplicates</div>
          <ul className="text-xs text-slate-600 space-y-0.5 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-3 bg-slate-50">
            {result.skipped.slice(0, 20).map((s, k) => (
              <li key={k}>
                <span className="font-mono">{s.legacyId || s.email}</span> — {s.companyName} (matched by {s.reason})
              </li>
            ))}
            {result.skipped.length > 20 && <li>…and {result.skipped.length - 20} more.</li>}
          </ul>
        </div>
      )}

      <div className="flex gap-3 justify-center mt-5">
        <Link to="/pipeline" className="btn-primary">Open Pipeline</Link>
        <button onClick={onAnother} className="btn-ghost border border-slate-200">Import another file</button>
      </div>
    </div>
  );
}

/* ------------------- small UI bits ------------------- */
function Stat({ label, value, accent }) {
  const map = {
    b1s:    'text-b1s-700',
    amber:  'text-amber-700',
    slate:  'text-slate-700'
  };
  return (
    <div className="card p-4">
      <div className="text-2xl font-bold text-brand-700">
        <span className={map[accent] || 'text-brand-700'}>{value}</span>
      </div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}
function Th({ children }) {
  return <th className="text-left px-3 py-2 font-medium">{children}</th>;
}
function Td({ children, className = '' }) {
  return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;
}
